import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import axios from "axios";
import { Phone, Mic, Play, Users } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Base numbers (fallbacks if the backend call fails or hasn't returned yet).
const dom = new Date().getDate();
const FALLBACK_STATS = {
  discovery_calls_total: 500 + dom,
  studio_sessions_total: 300 + Math.floor(dom / 2),
  podcast_downloads_k: 100 + dom,
  guests_featured: 150 + Math.floor(dom / 3),
};

const buildStats = (data) => [
  {
    key: "discovery",
    icon: Phone,
    color: "#22c55e",
    value: data.discovery_calls_total,
    suffix: "+",
    label: "Discovery calls booked",
    sublabel: "all-time",
  },
  {
    key: "sessions",
    icon: Mic,
    color: "#00f0ff",
    value: data.studio_sessions_total,
    suffix: "+",
    label: "Studio sessions produced",
    sublabel: "all-time",
  },
  {
    key: "downloads",
    icon: Play,
    color: "#a78bfa",
    value: data.podcast_downloads_k,
    suffix: "K+",
    label: "Podcast downloads",
    sublabel: "all-time",
  },
  {
    key: "guests",
    icon: Users,
    color: "#ff0055",
    value: data.guests_featured,
    suffix: "+",
    label: "Guests featured",
    sublabel: "on Spotlight",
  },
];

const CountUp = ({ target, decimals = 0, active, duration = 1600 }) => {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!active) return;
    // Skip the frame-by-frame count-up animation on mobile — the per-frame
    // re-render + text-shadow paint was causing black-flicker on iOS/Chrome.
    const isMobile =
      typeof window !== "undefined" &&
      window.matchMedia("(max-width: 768px)").matches;
    if (isMobile) {
      setValue(target);
      return;
    }
    let raf;
    const start = performance.now();
    const step = (t) => {
      const p = Math.min((t - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3); // easeOutCubic
      setValue(target * eased);
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [active, target, duration]);
  const formatted = decimals
    ? value.toFixed(decimals)
    : Math.round(value).toLocaleString();
  return <>{formatted}</>;
};

const StatStrip = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const [data, setData] = useState(FALLBACK_STATS);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/stats/public`, { timeout: 6000 })
      .then((res) => {
        if (!cancelled && res?.data) setData(res.data);
      })
      .catch(() => {
        /* keep fallback */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const stats = buildStats(data);

  return (
    <section
      ref={ref}
      data-testid="stat-strip"
      className="relative py-14 md:py-20"
      aria-label="SB Media Studio live stats"
    >
      <div className="absolute inset-0 -z-10 hidden md:block">
        <motion.div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[300px] rounded-full bg-[#00f0ff] blur-[140px]"
          animate={{ opacity: [0.05, 0.015, 0.05] }}
          transition={{ duration: 7, ease: "easeInOut", repeat: Infinity }}
        />
        <motion.div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[300px] rounded-full bg-[#ff0055] blur-[140px]"
          animate={{ opacity: [0.015, 0.05, 0.015] }}
          transition={{ duration: 7, ease: "easeInOut", repeat: Infinity }}
        />
      </div>

      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        {/* Glow wrapper — rotating conic gradient behind the panel border */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="relative"
        >
          {/* Even spotlight bathing the whole panel — pulses cyan ↔ magenta.
              Hidden on mobile: the radial-gradient background animation + 24px
              blur is a paint killer on iOS/Chrome mobile GPUs. */}
          <motion.div
            className="absolute -inset-16 pointer-events-none hidden md:block"
            aria-hidden="true"
            style={{ filter: "blur(24px)" }}
            animate={{
              background: [
                "radial-gradient(ellipse 60% 80% at 50% 50%, rgba(0,240,255,0.22) 0%, rgba(0,240,255,0.10) 45%, transparent 75%)",
                "radial-gradient(ellipse 60% 80% at 50% 50%, rgba(255,0,85,0.22) 0%, rgba(255,0,85,0.10) 45%, transparent 75%)",
                "radial-gradient(ellipse 60% 80% at 50% 50%, rgba(0,240,255,0.22) 0%, rgba(0,240,255,0.10) 45%, transparent 75%)",
              ],
            }}
            transition={{ duration: 7, ease: "easeInOut", repeat: Infinity }}
          />

          {/* Vertical light streaks — magenta on the left edge, cyan on the right edge.
              Hidden on mobile: the moving box-shadow + blur streaks cause visible
              flickering during scroll stops on mobile GPUs. */}
          <div
            className="absolute -inset-[3px] rounded-[26px] overflow-hidden pointer-events-none hidden md:block"
            aria-hidden="true"
            data-testid="stat-strip-glow"
          >
            {/* Magenta streak — travels top→bottom on the LEFT edge */}
            <motion.div
              className="absolute left-0 h-40 w-[6px]"
              style={{
                background:
                  "linear-gradient(to bottom, transparent, rgba(255,0,85,0.6) 30%, #ff0055 50%, #ffffff 55%, #ff0055 60%, rgba(255,0,85,0.6) 70%, transparent)",
                filter: "blur(3px)",
                boxShadow: "0 0 24px 6px rgba(255,0,85,0.55)",
              }}
              initial={{ top: "-15%" }}
              animate={{ top: ["-15%", "115%"] }}
              transition={{ duration: 3.2, ease: "linear", repeat: Infinity }}
            />
            {/* Cyan streak — travels top→bottom on the RIGHT edge */}
            <motion.div
              className="absolute right-0 h-40 w-[6px]"
              style={{
                background:
                  "linear-gradient(to bottom, transparent, rgba(0,240,255,0.6) 30%, #00f0ff 50%, #ffffff 55%, #00f0ff 60%, rgba(0,240,255,0.6) 70%, transparent)",
                filter: "blur(3px)",
                boxShadow: "0 0 24px 6px rgba(0,240,255,0.55)",
              }}
              initial={{ top: "-15%" }}
              animate={{ top: ["-15%", "115%"] }}
              transition={{
                duration: 3.2,
                ease: "linear",
                repeat: Infinity,
                delay: 1.6,
              }}
            />
          </div>

          {/* Actual stat panel — opaque on mobile (no backdrop-blur to avoid
              scroll re-rasterization); translucent + blurred on md+ */}
          <div className="relative rounded-3xl border border-white/10 bg-[#0a0510] md:bg-gradient-to-br md:from-[#0a0510]/95 md:to-[#11091a]/90 md:backdrop-blur-md px-6 md:px-10 py-8 md:py-10">
            <div className="flex items-center gap-3 mb-6 md:mb-8">
              <span className="relative flex w-2 h-2">
                <span className="absolute inline-flex w-full h-full rounded-full bg-[#22c55e] opacity-70 animate-ping" />
                <span className="relative inline-flex w-2 h-2 rounded-full bg-[#22c55e]" />
              </span>
              <span className="text-[10px] uppercase tracking-[0.32em] text-[#22c55e]">
                Live · The studio at a glance
              </span>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10">
              {stats.map((s, i) => {
                const Icon = s.icon;
                return (
                <motion.div
                  key={s.key}
                  initial={{ opacity: 0, y: 18 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{
                    duration: 0.6,
                    delay: 0.1 + i * 0.08,
                    ease: [0.16, 1, 0.3, 1],
                  }}
                  data-testid={`stat-${s.key}`}
                  className="group flex flex-col"
                >
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center mb-4 border"
                    style={{
                      backgroundColor: `${s.color}22`,
                      borderColor: `${s.color}55`,
                      color: s.color,
                    }}
                  >
                    <Icon size={16} />
                  </div>
                  <div className="font-display font-black text-3xl md:text-5xl tracking-tighter leading-none">
                    <span
                      className="text-glow-cyan"
                      style={{ color: s.color }}
                    >
                      <CountUp
                        target={s.value}
                        decimals={s.decimals}
                        active={inView}
                      />
                      {s.suffix}
                    </span>
                  </div>
                  <div className="mt-3 font-display font-semibold text-sm text-white/90">
                    {s.label}
                  </div>
                  <div className="text-[11px] uppercase tracking-[0.22em] text-[#a395c3]/70 mt-1">
                    {s.sublabel}
                  </div>
                </motion.div>
              );
            })}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default StatStrip;
